/**
 * THE THIRD RULED PROVIDER, and the one that is easy to lose.
 *
 * LOCKED D29 (docs/design/00-scope-v1.md) rules "sign-in v1: Apple + Google + e-mail magic
 * link". THREE, not two. The owner's sign-in drawing shows Apple and Google only — and the
 * prototype contains no sign-in pixel at all, so the drawing is intent delivered directly rather
 * than a scope cut (decision-log row 117). Dropping the magic link because a drawing omits it would
 * be reading intent as spec, which row 117 says not to do.
 *
 * ITS PREREQUISITES ARE SEPARATE FROM THE REDIRECT FALLBACK'S FOUR, and there are two: a continue
 * URL whose domain is in Firebase's authorized domains, and a handler route in the SPA that
 * finishes the sign-in (adr/0007-web-app-stack.md). The handler is folded into `/sign-in` rather
 * than given a route of its own, because the SPA has to read the stored address back on the same
 * screen anyway.
 *
 * THE SCHEMA ALREADY SEEDS ITS RATE LIMIT: `('magic_link','Request a magic link',8)` against the
 * `ip` and `firebase_uid` scopes (docs/design/04-schema.md). So a 429 on this path is a
 * designed answer and the screen says something useful about it rather than "unknown error".
 *
 * WHY IT IS BEHIND A DISCLOSURE RATHER THAN A THIRD BUTTON. Two provider buttons and a field is a
 * busier screen than the owner drew, and the magic link is the slowest of the three paths. It is
 * one keyboard-reachable press away, and `<summary>` is focusable and activatable for free.
 */
import { useState } from 'react';
import { Alert, Button, Stack, Text, TextInput } from '@mantine/core';
import { Trans, useTranslation } from '../../../i18n';
import { looksLikeEmailAddress, rememberMagicLinkAddress } from '../session/magic-link-address';
import classes from './auth-shell.module.css';

export interface MagicLinkPanelProps {
  readonly onSend: (email: string) => Promise<void>;
  readonly disabled?: boolean;
}

type SendState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'sending' }
  | { readonly kind: 'sent'; readonly email: string }
  | { readonly kind: 'failed' };

export function MagicLinkPanel({ onSend, disabled = false }: MagicLinkPanelProps) {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [state, setState] = useState<SendState>({ kind: 'idle' });
  const [notAnAddress, setNotAnAddress] = useState(false);

  const send = async (): Promise<void> => {
    const trimmed = email.trim();
    if (!looksLikeEmailAddress(trimmed)) {
      setNotAnAddress(true);
      return;
    }
    setNotAnAddress(false);
    setState({ kind: 'sending' });
    try {
      await onSend(trimmed);
      // Stored BEFORE the confirmation is shown: if the person opens the link on this device, the
      // handler needs it, and there is no second chance to write it down.
      rememberMagicLinkAddress(trimmed);
      setState({ kind: 'sent', email: trimmed });
    } catch {
      setState({ kind: 'failed' });
    }
  };

  if (state.kind === 'sent') {
    return (
      <Alert role="status" variant="light" color="green" title={t('magicLink.sent.title')}>
        <Stack gap="xs">
          <Text size="sm">
            <Trans
              t={t}
              i18nKey="magicLink.sent.body"
              values={{ email: state.email }}
              components={{ strong: <strong /> }}
            />
          </Text>
          <Text size="sm" c="dimmed">
            {t('magicLink.sent.elsewhere')}
          </Text>
        </Stack>
      </Alert>
    );
  }

  return (
    // A quiet, centred line under the two buttons, as the doors' card draws it (design 15).
    <details className={classes.disclosure}>
      <summary>{t('magicLink.disclosure')}</summary>
      <Stack gap="sm" pt="sm">
        <TextInput
          type="email"
          label={t('magicLink.emailLabel')}
          description={t('magicLink.emailDescription')}
          placeholder={t('magicLink.emailPlaceholder')}
          value={email}
          onChange={(event) => {
            setEmail(event.currentTarget.value);
            setNotAnAddress(false);
          }}
          // Mantine links label, description and error to the input itself; that is the whole
          // reason inputs go through its wrapper rather than a hand-rolled one.
          error={notAnAddress ? t('magicLink.notAnAddress') : null}
          disabled={disabled || state.kind === 'sending'}
          autoComplete="email"
        />
        <Button
          variant="default"
          radius="xl"
          onClick={() => {
            void send();
          }}
          loading={state.kind === 'sending'}
          disabled={disabled}
        >
          {t('magicLink.send')}
        </Button>
        {state.kind === 'failed' ? (
          <Alert role="alert" variant="light" color="red" title={t('magicLink.failed.title')}>
            <Text size="sm">{t('magicLink.failed.body')}</Text>
          </Alert>
        ) : null}
      </Stack>
    </details>
  );
}
